import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Mail, Calendar as CalIcon, DollarSign, Trash2, Plus, CheckSquare, CalendarDays, CalendarClock, MessageSquare, Edit2, Eye, Save, X } from "lucide-react";
import { motion } from "framer-motion";
import { Calendar } from "@/components/ui/calendar";
import { SkeletonTable, SkeletonText, Skeleton } from "@/components/ui/skeleton";

const SITE_URL = "https://niskyauto.live";

function StatusPill({ status }) {
  const map = {
    pending:   { c: "#FF9800", l: "Pending" },
    confirmed: { c: "#4CAF50", l: "Confirmed" },
    cancelled: { c: "#F44336", l: "Cancelled" },
    completed: { c: "#2196F3", l: "Completed" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] rounded-full"
      style={{ color: s.c, background: `${s.c}14`, boxShadow: `inset 0 0 0 1px ${s.c}33` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.c }}></span>
      {s.l}
    </span>
  );
}

// Times for reschedule picker
const RESCHEDULE_TIMES = [];
for (let h = 9; h <= 18; h++) {
  RESCHEDULE_TIMES.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 18) RESCHEDULE_TIMES.push(`${String(h).padStart(2, "0")}:30`);
}

function to12h(t) {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function truncateCarName(name) {
  if (!name) return "";
  return name.length > 10 ? name.slice(0, 7) + "..." : name;
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [acting, setActing] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  // Confirm-with-splits
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [splits, setSplits] = useState([]);

  // Edit-splits popup
  const [splitsOpen, setSplitsOpen] = useState(false);
  const [editSplits, setEditSplits] = useState([]);

  // Price update
  const [priceOpen, setPriceOpen] = useState(false);
  const [newPrice, setNewPrice] = useState("");
  const [priceReason, setPriceReason] = useState("");

  // Admin reschedule
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [closedDates, setClosedDates] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState({});
  const [fullyBlocked, setFullyBlocked] = useState([]);

  // Client reschedule request response
  const [respondOpen, setRespondOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");

  const [isOwner, setIsOwner] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, u, me] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/users"), api.get("/admin/me")]);
      setBookings(b.data.bookings || []);
      setAdmins(u.data.users || []);
      setIsOwner(me.data?.role === "owner");
    } catch (e) { toast.error(errMsg(e, "Failed to load")); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/closed-dates").then(({ data }) => setClosedDates(data.closed_dates || [])).catch(() => {});
    api.get("/availability").then(({ data }) => {
      setBlockedSlots(data.blocked_slots || {});
      setFullyBlocked(data.fully_blocked_dates || []);
    }).catch(() => {});
  }, []);

  const openConfirm = (b) => {
    setActive(b);
    setSplits(b.splits?.length ? b.splits : []);
    setConfirmOpen(true);
  };

  const submitConfirm = async () => {
    if (splits.length === 0) return toast.error("Assign at least one worker");
    const total = splits.reduce((a, s) => a + (parseFloat(s.percent) || 0), 0);
    if (Math.abs(total - 100) > 0.5) return toast.error(`Splits must total 100% (got ${total.toFixed(0)}%)`);
    setActing(true);
    try {
      await api.post(`/admin/bookings/${active.id}/confirm`, {
        splits: splits.map((s) => ({ ...s, percent: parseFloat(s.percent) })),
      });
      toast.success("Confirmed — client & all admins notified");
      setConfirmOpen(false); setActive(null); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const submitSplits = async () => {
    if (editSplits.length === 0) return toast.error("At least one worker is required");
    const total = editSplits.reduce((a, s) => a + (parseFloat(s.percent) || 0), 0);
    if (Math.abs(total - 100) > 0.5) return toast.error(`Splits must total 100% (got ${total.toFixed(0)}%)`);
    if (editSplits.some((s) => !s.uid)) return toast.error("Pick a worker for every split");
    setActing(true);
    try {
      await api.post(`/admin/bookings/${active.id}/splits`, {
        splits: editSplits.map((s) => ({ ...s, percent: parseFloat(s.percent) })),
      });
      toast.success("Splits updated — all admins notified");
      setSplitsOpen(false);
      setActive((prev) => ({ ...prev, splits: editSplits.map((s) => ({ ...s, percent: parseFloat(s.percent) })) }));
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const cancel = async (b) => {
    setActing(true);
    try {
      await api.post(`/admin/bookings/${b.id}/cancel`, { reason: cancelReason });
      toast.success("Cancelled"); setActive(null); setCancelReason(""); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeResult, setCompleteResult] = useState(null);

  const markComplete = async (b) => {
    setActive(b);
    setCompleteOpen(true);
  };

  const submitComplete = async () => {
    const b = active;
    if (!b) return;
    setActing(true);
    try {
      const resp = await api.post(`/admin/bookings/${b.id}/complete`);
      const data = resp.data;
      const enriched = data?.splits || [];
      setCompleteResult({ booking: { ...b, ...data }, splits: enriched });
      setCompleteOpen(false);
      toast.success("Marked complete — invoice sent to client");
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Permanently delete booking ${b.id}? This will remove all data and the client's Firebase account. This cannot be undone.`)) return;
    setActing(true);
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      toast.success("Booking permanently deleted");
      setActive(null); load();
    } catch (e) { toast.error(errMsg(e, "Failed to delete")); }
    finally { setActing(false); }
  };

  const updatePrice = async () => {
    setActing(true);
    try {
      await api.post(`/admin/bookings/${active.id}/price`, {
        new_price: parseFloat(newPrice), reason: priceReason,
      });
      toast.success("Price updated · customer notified");
      setPriceOpen(false); setNewPrice(""); setPriceReason(""); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const submitReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) return toast.error("Pick a date and time");
    setActing(true);
    try {
      await api.post(`/admin/bookings/${active.id}/reschedule`, {
        new_date: fmtDate(rescheduleDate),
        new_time: rescheduleTime,
      });
      toast.success("Reschedule request sent to client");
      setRescheduleOpen(false); setRescheduleDate(null); setRescheduleTime(""); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const respondReschedule = async (action) => {
    if (action === "cancel" && !window.confirm("Cancel this booking due to the scheduling conflict?")) return;
    setActing(true);
    try {
      await api.post(`/admin/bookings/${active.id}/respond-reschedule`, { action });
      toast.success(action === "accept" ? "Reschedule accepted — client notified" : "Booking cancelled");
      setRespondOpen(false); setActive(null); load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const sendMessageToClient = async () => {
    if (!msgBody.trim()) return toast.error("Enter a message");
    setActing(true);
    try {
      await api.post(`/admin/bookings/${active.id}/message`, {
        subject: msgSubject.trim() || undefined,
        message: msgBody.trim(),
      });
      toast.success("Message sent to client");
      setMsgOpen(false); setMsgSubject(""); setMsgBody("");
    } catch (e) { toast.error(errMsg(e, "Failed")); }
    finally { setActing(false); }
  };

  const updateService = async (bookingId, service) => {
    try {
      await api.patch(`/admin/bookings/${bookingId}/service`, { service });
      toast.success("Service updated");
      setActive((a) => a ? { ...a, service } : a);
      load();
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };

  const isOutsideSeason = (d) => {
    const y = d.getFullYear();
    const start = new Date(y, 5, 18);
    const end = new Date(y, 8, 6);
    return d < start || d > end;
  };

  const calDisabled = (d) => {
    const dStr = fmtDate(d);
    const today = new Date(); today.setHours(0,0,0,0);
    if (d < today) return true;
    if (isOutsideSeason(d)) return true;
    if (closedDates.includes(dStr)) return true;
    if (fullyBlocked.includes(dStr)) return true;
    return false;
  };

  const blockedForRescheduleDate = rescheduleDate ? (blockedSlots[fmtDate(rescheduleDate)] || []) : [];

  return (
    <div data-testid="admin-bookings">
      <div className="flex items-end justify-between mb-5">
        <div>
          <p className="overline mb-2">Bookings</p>
          <h1 className="font-sans font-semibold text-4xl text-foreground tracking-[-0.02em]">All client bookings</h1>
        </div>
        <button onClick={load} className="text-xs text-muted hover:text-foreground">Refresh</button>
      </div>

      {loading ? (
        <SkeletonTable rows={6} columns={6} />
      ) : bookings.length === 0 ? (
        <div className="card-luxe p-8 text-center">
          <CalIcon size={32} className="text-muted mx-auto mb-3" />
          <p className="text-muted">No bookings yet</p>
        </div>
      ) : (
        <div
          className="card-luxe overflow-x-auto overflow-y-auto"
          style={{ maxHeight: "70vh" }}
          data-testid="admin-bookings-list"
        >
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10" style={{ background: "var(--bg-elevated)" }}>
              <tr className="text-left">
                <th className="px-5 py-1 overline">Booking</th>
                <th className="px-5 py-1 overline">Date</th>
                <th className="px-5 py-1 overline">Client</th>
                <th className="px-5 py-1 overline">Vehicle</th>
                <th className="px-5 py-1 overline">Service</th>
                <th className="px-5 py-1 overline">Total</th>
                <th className="px-5 py-1 overline">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr
                  key={b.id}
                  onClick={() => setActive(b)}
                  className="cursor-pointer hover:bg-foreground/[0.04] transition-colors"
                  data-testid={`admin-booking-row-${b.id}`}
                >
                  <td className="px-5 py-1 font-mono text-foreground">{b.id}</td>
                   <td className="px-5 py-1 text-foreground">{b.appointment_date} <span className="text-faint">{to12h(b.appointment_time)}</span></td>
                  <td className="px-5 py-1 text-foreground">{b.contact?.name}</td>
                  <td className="px-5 py-1" style={{ color: "#b8b8c0" }} title={`${b.car?.make || ""} ${b.car?.model || ""}`.trim()}>
                    {truncateCarName(b.car?.make)} {truncateCarName(b.car?.model)}
                  </td>
                  <td className="px-5 py-1 text-faint text-xs">{({ full: "Full", interior: "Int.", exterior: "Ext." })[b.service] || "Full"}</td>
                  <td className="px-5 py-1 font-mono text-foreground">${b.price?.estimate}</td>
                  <td className="px-5 py-1"><StatusPill status={b.status} />{b.pending_reschedule?.requested_by === "client" && <span title="Client reschedule request" className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: "#FF9800", background: "rgba(255,152,0,0.12)", boxShadow: "inset 0 0 0 1px rgba(255,152,0,0.3)" }}><CalendarClock size={10} /> request</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Booking detail modal */}
      {active && !confirmOpen && !priceOpen && !rescheduleOpen && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setActive(null)}></div>
          <div className="relative card-luxe p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="overline mb-1">Booking</p>
                <p className="font-mono text-2xl text-foreground">{active.id}</p>
              </div>
              <StatusPill status={active.status} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
              <Info label="Client">{active.contact?.name}</Info>
              <Info label="Email">{active.contact?.email && <a href={`mailto:${active.contact.email}`} className="text-foreground hover:underline">{active.contact.email}</a>}</Info>
              <Info label="Phone">{active.contact?.phone}</Info>
              <Info label="Price"><span className="font-mono">${active.price?.estimate}</span></Info>
              <Info label="Date">{active.appointment_date}</Info>
              <Info label="Time">{to12h(active.appointment_time)}</Info>
              <Info label="Vehicle">{active.car?.year || ""} {active.car?.make} {active.car?.model}</Info>
              <Info label="Condition" className="capitalize">{active.condition}</Info>
              <Info label="Address" className="sm:col-span-2">{active.address?.formatted}</Info>
              <div className="sm:col-span-2">
                <p className="overline mb-1">Service</p>
                <ServiceEditor
                  value={active.service || "full"}
                  onChange={(svc) => updateService(active.id, svc)}
                />
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="overline">Splits</span>
                  {isOwner && (
                    <button
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors"
                      style={{ color: "rgba(245,245,247,0.5)" }}
                      onClick={() => { setEditSplits((active.splits || []).map((s) => ({ ...s }))); setSplitsOpen(true); }}
                    >
                      <Edit2 size={12} /> {active.splits?.length ? "Edit" : "Assign"}
                    </button>
                  )}
                </div>
                {active.splits?.length > 0 ? (
                  <p className="text-foreground text-sm">
                    {active.splits.map((s) => `${s.email} · ${s.percent}%`).join(" · ")}
                  </p>
                ) : (
                  <p className="text-sm" style={{ color: "rgba(245,245,247,0.35)" }}>Not assigned</p>
                )}
              </div>
              {active.pending_reschedule && (
                <Info label="Pending Reschedule" className="sm:col-span-2">
                  <span className="text-gold">
                    {active.pending_reschedule.new_date} at {to12h(active.pending_reschedule.new_time)}
                    {active.pending_reschedule.requested_by === "client" ? " (client request)" : " (admin request, awaiting client)"}
                  </span>
                </Info>
              )}
            </div>
            {active.pending_reschedule?.requested_by === "client" && (
              <div className="mb-4 p-4 rounded-xl" style={{ background: "rgba(255,152,0,0.07)", boxShadow: "inset 0 0 0 1px rgba(255,152,0,0.25)" }}>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock size={15} style={{ color: "#FF9800" }} />
                  <p className="text-sm font-medium" style={{ color: "#FF9800" }}>Client requests reschedule</p>
                </div>
                <p className="text-sm text-muted mb-3">
                  <strong className="text-foreground">{active.pending_reschedule.new_date}</strong> at <strong className="text-foreground">{to12h(active.pending_reschedule.new_time)}</strong>
                  {active.pending_reschedule.reason && <><br /><em>"{active.pending_reschedule.reason}"</em></>}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button className="btn-primary" style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => respondReschedule("accept")} disabled={acting}>
                    <CheckCircle2 size={13} /> Accept
                  </button>
                  <button className="btn-secondary" style={{ fontSize: 12, padding: "6px 14px" }} onClick={() => setMsgOpen(true)}>
                    <MessageSquare size={13} /> Message client
                  </button>
                  <button className="btn-secondary" style={{ fontSize: 12, padding: "6px 14px", color: "#F44336" }} onClick={() => respondReschedule("cancel")} disabled={acting}>
                    <XCircle size={13} /> Cancel booking
                  </button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted mb-2">
              Status page:{" "}
              <a href={`${SITE_URL}/status?id=${active.id}`} target="_blank" rel="noreferrer" className="text-gold underline">
                {SITE_URL}/status?id={active.id}
              </a>
            </p>
            {active.status !== "cancelled" && active.status !== "completed" && (
              <>
                <div className="space-y-3 mb-4">
                  <textarea className="input-luxe" rows={2} placeholder="Cancel reason (sent to client)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  {active.status === "pending" && (
                    <button className="btn-primary" onClick={() => openConfirm(active)} disabled={acting}>
                      <CheckCircle2 size={14} /> Confirm + assign
                    </button>
                  )}
                  {(active.status === "confirmed" || active.status === "pending") && (
                    <button className="btn-primary" onClick={() => markComplete(active)} disabled={acting}
                      style={{ background: "#2196F3" }}>
                      <CheckSquare size={14} /> Mark as Complete
                    </button>
                  )}
                  <button className="btn-secondary" onClick={() => setRescheduleOpen(true)}>
                    <CalendarDays size={14} /> Reschedule
                  </button>
                  <button className="btn-secondary" onClick={() => { setNewPrice(active.price?.estimate || ""); setPriceOpen(true); }}>
                    <DollarSign size={14} /> Change price
                  </button>
                  {active.contact?.email && <a className="btn-secondary" href={`mailto:${active.contact.email}`}><Mail size={14} /> Email</a>}
                  <button className="btn-secondary" style={{ color: "#F44336" }} onClick={() => cancel(active)} disabled={acting}>
                    <XCircle size={14} /> Cancel
                  </button>
                </div>
              </>
            )}
            {active.status === "completed" && (
              <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
                <p className="text-sm text-muted">Job completed. Invoice sent to client.</p>
                {isOwner && (
                  <button
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
                    style={{ color: "#F44336", background: "rgba(244,67,54,0.06)", boxShadow: "inset 0 0 0 1px rgba(244,67,54,0.35)" }}
                    onClick={() => deleteBooking(active)}
                    disabled={acting}
                  >
                    <Trash2 size={13} /> Delete permanently
                  </button>
                )}
              </div>
            )}
            {active.status === "cancelled" && isOwner && (
              <div className="flex justify-end mt-3">
                <button
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
                  style={{ color: "#F44336", background: "rgba(244,67,54,0.06)", boxShadow: "inset 0 0 0 1px rgba(244,67,54,0.35)" }}
                  onClick={() => deleteBooking(active)}
                  disabled={acting}
                >
                  <Trash2 size={13} /> Delete permanently
                </button>
              </div>
            )}
            <button className="absolute top-4 right-4 text-muted hover:text-foreground" onClick={() => setActive(null)}>✕</button>
          </div>
        </motion.div>
      )}

      {/* Confirm with splits modal */}
      {confirmOpen && active && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="confirm-splits-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setConfirmOpen(false)}></div>
          <div className="relative card-luxe p-5 max-w-lg w-full">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-2">Assign workers</h3>
            <p className="text-muted text-sm mb-4">Choose who's working this job and their split. Splits must total 100%. The client and all admins will receive a confirmation email.</p>
            <div className="space-y-2 mb-3">
              {splits.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select className="input-luxe flex-1" value={s.uid} onChange={(e) => {
                    const u = admins.find((a) => a.uid === e.target.value);
                    const newSplits = [...splits];
                    newSplits[i] = { uid: e.target.value, email: u?.email || "", percent: s.percent };
                    setSplits(newSplits);
                  }} data-testid={`split-uid-${i}`}>
                    <option value="">Pick worker…</option>
                    {admins.map((a) => <option key={a.uid} value={a.uid}>{a.email}</option>)}
                  </select>
                  <input type="number" min="0" max="100" step="1" className="input-luxe w-24" placeholder="%" value={s.percent} onChange={(e) => {
                    const newSplits = [...splits];
                    newSplits[i] = { ...s, percent: e.target.value };
                    setSplits(newSplits);
                  }} data-testid={`split-pct-${i}`} />
                  <button onClick={() => setSplits(splits.filter((_, idx) => idx !== i))} className="text-faint hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <button className="btn-secondary mb-4" style={{ padding: "8px 14px", fontSize: "12px" }}
              onClick={() => setSplits([...splits, { uid: "", email: "", percent: 0 }])}
              data-testid="split-add">
              <Plus size={12} /> Add worker
            </button>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setConfirmOpen(false)}>Back</button>
              <button className="btn-primary" onClick={submitConfirm} disabled={acting} data-testid="split-submit">
                {acting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Confirm booking
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Edit splits popup */}
      {splitsOpen && active && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="splits-popup">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setSplitsOpen(false)}></div>
          <div className="relative card-luxe p-5 max-w-lg w-full">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-2">
              {isOwner ? "Edit" : "View"} splits
            </h3>
            {!isOwner && (
              <p className="text-xs px-3 py-2 rounded mb-4" style={{ background: "rgba(255,152,0,0.1)", color: "#FF9800" }}>
                Read only — only owners can change splits.
              </p>
            )}
            <div className="space-y-2 mb-3">
              {editSplits.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select
                    className="input-luxe flex-1"
                    value={s.uid}
                    disabled={!isOwner}
                    onChange={(e) => {
                      const u = admins.find((a) => a.uid === e.target.value);
                      const next = [...editSplits];
                      next[i] = { uid: e.target.value, email: u?.email || e.target.options[e.target.selectedIndex]?.text || "", percent: s.percent };
                      setEditSplits(next);
                    }}
                    data-testid={`split-uid-${i}`}
                  >
                    <option value="">{s.email || "Pick worker…"}</option>
                    {admins.map((a) => (
                      <option key={a.uid} value={a.uid}>{a.email}</option>
                    ))}
                  </select>
                  <input
                    type="number" min="0" max="100" step="1"
                    className="input-luxe w-24"
                    placeholder={String(s.percent)}
                    value={s.percent}
                    disabled={!isOwner}
                    onChange={(e) => {
                      const next = [...editSplits];
                      next[i] = { ...s, percent: e.target.value };
                      setEditSplits(next);
                    }}
                    data-testid={`split-pct-${i}`}
                    style={!isOwner ? { opacity: 0.5 } : {}}
                  />
                  {isOwner && (
                    <button
                      onClick={() => setEditSplits(editSplits.filter((_, idx) => idx !== i))}
                      className="text-faint hover:text-red-400 p-2"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isOwner && (
              <button
                className="btn-secondary mb-4"
                style={{ padding: "8px 14px", fontSize: "12px" }}
                onClick={() => setEditSplits([...editSplits, { uid: "", email: "", percent: 0 }])}
                data-testid="split-add"
              >
                <Plus size={12} /> Add worker
              </button>
            )}
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setSplitsOpen(false)}>Cancel</button>
              {isOwner && (
                <button className="btn-primary" onClick={submitSplits} disabled={acting} data-testid="split-save">
                  {acting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Reschedule modal */}
      {rescheduleOpen && active && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setRescheduleOpen(false)}></div>
          <div className="relative card-luxe p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-2">Request Reschedule</h3>
            <p className="text-muted text-sm mb-4">Choose a new date and time. The client will receive an email asking them to accept, cancel, or choose a different date.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="overline mb-3">New Date</p>
                <Calendar
                  mode="single"
                  selected={rescheduleDate}
                  onSelect={setRescheduleDate}
                  disabled={calDisabled}
                  defaultMonth={new Date(new Date().getFullYear(), new Date().getMonth())}
                  className="bg-transparent text-foreground"
                />
              </div>
              <div>
                <p className="overline mb-3">New Time</p>
                {!rescheduleDate ? (
                  <p className="text-muted text-sm">Pick a date first.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-1 max-h-64 overflow-y-auto">
                    {RESCHEDULE_TIMES.map((t) => {
                      const isBlocked = blockedForRescheduleDate.includes(t);
                      const active2 = rescheduleTime === t;
                      return (
                        <button
                          key={t}
                          onClick={() => !isBlocked && setRescheduleTime(t)}
                          disabled={isBlocked}
                          style={isBlocked
                            ? { color: "rgba(245,245,247,0.20)", boxShadow: "inset 0 0 0 1px rgba(245,245,247,0.06)", cursor: "not-allowed", textDecoration: "line-through" }
                            : active2
                              ? { background: "var(--text)", color: "var(--bg)", boxShadow: "inset 0 0 0 1px rgba(245,245,247,0.20)" }
                              : { color: "rgba(245,245,247,0.70)", boxShadow: "inset 0 0 0 1px rgba(245,245,247,0.10)" }
                          }
                          className="px-2 py-1.5 text-xs rounded transition-all"
                        >{to12h(t)}</button>
                      );
                    })}
      {/* Complete confirmation popup */}
      {completeOpen && active && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setCompleteOpen(false)}></div>
          <div className="relative card-luxe p-5 max-w-md w-full">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-2">Complete booking</h3>
            <p className="text-muted text-sm mb-3">Review the payment breakdown before marking this job as complete. An invoice will be emailed to the client.</p>
            <div className="space-y-1 mb-3">
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "rgba(245,245,247,0.5)" }}>Booking</span><span>{active.id}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "rgba(245,245,247,0.5)" }}>Price</span><span>${active.price?.estimate}</span></div>
              {active.splits?.length > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, alignItems: "flex-start" }}>
                  <span style={{ color: "rgba(245,245,247,0.5)" }}>Splits</span>
                  <span style={{ textAlign: "right" }}>
                    {active.splits.map((s) => (
                      <div key={s.uid || s.email}>{s.email} · {s.percent}% (${((active.price?.estimate || 0) * s.percent / 100).toFixed(0)})</div>
                    ))}
                  </span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted mb-3" style={{ lineHeight: 1.5 }}>
              Workers' outstanding expense debt will be auto-deducted from their gross share. Payouts will be created for each worker to confirm.
            </p>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setCompleteOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={submitComplete} disabled={acting}
                style={{ background: "#2196F3" }}>
                {acting ? <Loader2 size={14} className="animate-spin" /> : <CheckSquare size={14} />} Mark Complete — ${active.price?.estimate}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Complete result popup */}
      {completeResult && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setCompleteResult(null)}></div>
          <div className="relative card-luxe p-5 max-w-md w-full">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-1">Booking completed</h3>
            <p className="text-muted text-sm mb-3">Invoice sent to client. Here's the final payment breakdown:</p>
            <div className="space-y-1 mb-3">
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "rgba(245,245,247,0.5)" }}>Booking</span><span>{completeResult.booking.id}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "rgba(245,245,247,0.5)" }}>Total price</span><span>${completeResult.booking.price?.estimate}</span></div>
            </div>
            {completeResult.splits.length > 0 && (
              <>
                <p className="overline mb-2">Worker payouts</p>
                {completeResult.splits.map((s) => (
                  <div key={s.uid || s.email} style={{ padding: '10px', borderRadius: '8px', background: 'rgba(245,245,247,0.03)', marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "rgba(245,245,247,0.5)" }}>Worker</span><span>{s.email}</span></div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "rgba(245,245,247,0.5)" }}>Gross</span><span>${s.gross}</span></div>
                    {s.debt_applied > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: '#FF9800' }}>Expense debt</span><span style={{ color: '#FF9800' }}>−${s.debt_applied}</span></div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, borderTop: '1px solid rgba(245,245,247,0.1)', paddingTop: 4, marginTop: 4 }}>
                      <span><strong>Net payout</strong></span>
                      <span><strong>${s.net_payout}</strong></span>
                    </div>
                  </div>
                ))}
              </>
            )}
            <div className="flex justify-end">
              <button className="btn-primary" onClick={() => setCompleteResult(null)}>Done</button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setRescheduleOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={submitReschedule} disabled={acting || !rescheduleDate || !rescheduleTime}>
                {acting ? <Loader2 size={14} className="animate-spin" /> : <CalendarDays size={14} />} Request Reschedule
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Price change modal */}
      {priceOpen && active && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="price-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setPriceOpen(false)}></div>
          <div className="relative card-luxe p-5 max-w-md w-full">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-2">Change booking price</h3>
            <p className="text-muted text-sm mb-4">The customer will be notified by email.</p>
            <label className="overline block mb-2">New price (USD)</label>
            <input type="number" step="0.01" min="0" className="input-luxe mb-4" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} data-testid="price-input" />
            <label className="overline block mb-2">Reason (optional)</label>
            <textarea className="input-luxe mb-4" rows={2} value={priceReason} onChange={(e) => setPriceReason(e.target.value)} placeholder="Why is the price changing?" />
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setPriceOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={updatePrice} disabled={acting || !newPrice} data-testid="price-submit">
                {acting ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />} Update & notify
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Message client modal */}
      {msgOpen && active && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setMsgOpen(false)}></div>
          <div className="relative card-luxe p-5 max-w-md w-full">
            <h3 className="font-sans font-semibold text-2xl text-foreground mb-2">Message client</h3>
            <p className="text-muted text-sm mb-4">Send a custom email to <strong>{active.contact?.email}</strong> via Brevo.</p>
            <label className="overline block mb-2">Subject (optional)</label>
            <input className="input-luxe mb-3" placeholder={`Update on your booking · ${active.id}`} value={msgSubject} onChange={(e) => setMsgSubject(e.target.value)} />
            <label className="overline block mb-2">Message</label>
            <textarea className="input-luxe mb-4" rows={5} placeholder="Your message to the client…" value={msgBody} onChange={(e) => setMsgBody(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setMsgOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={sendMessageToClient} disabled={acting || !msgBody.trim()}>
                {acting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send email
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

const SERVICE_OPTIONS = [
  { v: "full",     l: "Full Service Package" },
  { v: "interior", l: "Interior Reset" },
  { v: "exterior", l: "Exterior Car Wash" },
];

const SERVICE_LABELS = { full: "Full Service Package", interior: "Interior Reset", exterior: "Exterior Car Wash" };

function ServiceEditor({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const display = SERVICE_LABELS[value] || "Not set";

  if (!editing) return (
    <button
      onClick={() => setEditing(true)}
      className="flex items-center gap-1.5 text-sm text-foreground hover:text-gold transition-colors"
      style={{ textDecoration: "underline dotted", textUnderlineOffset: 3 }}
    >
      {display}
      <Edit2 size={11} className="text-muted" />
    </button>
  );

  return (
    <div className="flex items-center gap-2">
      <select
        className="input-luxe"
        style={{ fontSize: 13, padding: "5px 10px", height: 32 }}
        defaultValue={value || "full"}
        onChange={(e) => {
          onChange(e.target.value);
          setEditing(false);
        }}
      >
        {SERVICE_OPTIONS.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
      <button className="text-muted hover:text-foreground text-sm" onClick={() => setEditing(false)}>✕</button>
    </div>
  );
}

function Info({ label, children, className = "" }) {
  return (
    <div className={className}>
      <p className="overline mb-1">{label}</p>
      <p className="text-foreground text-sm">{children}</p>
    </div>
  );
}
